import * as React from 'react';
const GROUP_SELECTOR = '[role="group"]';
const GROUP_SELECT_ALL_SELECTOR = '[data-select-group-all]:not(:disabled)';
const OPTION_SELECTOR = '[role="option"]';
const ENABLED_OPTION_SELECTOR = `${OPTION_SELECTOR}:not(:disabled)`;
const SCROLL_ALIGNMENT_OPTIONS: ScrollIntoViewOptions = {
	block: 'nearest',
	inline: 'nearest',
};

interface IUseGroupedSelectKeyboardNavigationProps {
	clearHighlightedOption: () => void;
	focusTargetRef: React.RefObject<HTMLElement>;
	hideHighlight: () => void;
	highlightedGroupSelectAllId?: string;
	highlightedIndex: number;
	menuRef: React.RefObject<HTMLElement>;
	preserveFocus?: boolean;
	setHighlightedGroupSelectAllId: (id: string | undefined) => void;
	setHighlightedIndex: (index: number) => void;
	showHighlight: () => void;
}

const getButton = (element: Element | null | undefined): HTMLButtonElement | null =>
	element instanceof HTMLButtonElement ? element : null;

const getGroup = (element: Element | null): HTMLElement | null => {
	const group = element?.closest(GROUP_SELECTOR);
	return group instanceof HTMLElement ? group : null;
};

/**
 * Adds group select-all controls to the ArrowUp and ArrowDown navigation order.
 */
export const useGroupedSelectKeyboardNavigation = ({
	clearHighlightedOption,
	focusTargetRef,
	hideHighlight,
	highlightedGroupSelectAllId,
	highlightedIndex,
	menuRef,
	preserveFocus = false,
	setHighlightedGroupSelectAllId,
	setHighlightedIndex,
	showHighlight,
}: IUseGroupedSelectKeyboardNavigationProps): ((event: React.KeyboardEvent<HTMLElement>) => boolean) =>
	React.useCallback(
		event => {
			const isArrowKey = event.key === 'ArrowDown' || event.key === 'ArrowUp';
			const isBoundaryKey = event.key === 'Home' || event.key === 'End';
			if (!isArrowKey && !isBoundaryKey && (!preserveFocus || event.key !== 'Enter')) {
				return false;
			}

			const menu = menuRef.current;
			if (!menu) {
				return false;
			}

			const options = Array.from(menu.querySelectorAll<HTMLButtonElement>(OPTION_SELECTOR));
			const enabledOptions = options.filter(option => !option.disabled);
			const eventTarget = getButton(event.target instanceof Element ? event.target : null);
			const highlightedGroupSelectAll = highlightedGroupSelectAllId
				? getButton(document.getElementById(highlightedGroupSelectAllId))
				: null;
			const focusedGroupSelectAll =
				highlightedGroupSelectAll && menu.contains(highlightedGroupSelectAll)
					? highlightedGroupSelectAll
					: null;
			const activeGroupSelectAll =
				eventTarget?.matches(GROUP_SELECT_ALL_SELECTOR) === true ? eventTarget : null;
			const clearGroupSelectAllHighlight = (): void => {
				setHighlightedGroupSelectAllId(undefined);
			};

			if (isBoundaryKey && activeGroupSelectAll) {
				clearGroupSelectAllHighlight();
				focusTargetRef.current?.focus();
				return false;
			}

			if (!isArrowKey && focusedGroupSelectAll) {
				event.preventDefault();
				event.stopPropagation();
				focusedGroupSelectAll.click();
				setHighlightedGroupSelectAllId(focusedGroupSelectAll.id || undefined);
				focusTargetRef.current?.focus();
				return true;
			}

			const focusOption = (option: HTMLButtonElement | undefined): boolean => {
				if (!option) {
					return false;
				}

				const optionIndex = options.indexOf(option);
				if (optionIndex < 0) {
					return false;
				}

				event.preventDefault();
				event.stopPropagation();
				clearGroupSelectAllHighlight();
				setHighlightedIndex(optionIndex);
				showHighlight();
				focusTargetRef.current?.focus();
				return true;
			};

			const highlightGroupSelectAll = (groupSelectAll: HTMLButtonElement): boolean => {
				event.preventDefault();
				event.stopPropagation();
				hideHighlight();
				clearHighlightedOption();
				clearGroupSelectAllHighlight();
				setHighlightedGroupSelectAllId(groupSelectAll.id || undefined);
				if (preserveFocus) {
					focusTargetRef.current?.focus();
				} else {
					groupSelectAll.focus();
				}
				groupSelectAll.scrollIntoView(SCROLL_ALIGNMENT_OPTIONS);
				return true;
			};

			const navigatedGroupSelectAll = activeGroupSelectAll ?? focusedGroupSelectAll;
			if (navigatedGroupSelectAll) {
				const group = getGroup(navigatedGroupSelectAll);
				if (event.key === 'ArrowDown') {
					return focusOption(
						getButton(group?.querySelector(ENABLED_OPTION_SELECTOR)) ?? undefined
					);
				}

				const firstGroupOption = getButton(group?.querySelector(ENABLED_OPTION_SELECTOR));
				const firstGroupOptionPosition = firstGroupOption
					? enabledOptions.indexOf(firstGroupOption)
					: -1;
				const previousOption =
					firstGroupOptionPosition > 0
						? enabledOptions[firstGroupOptionPosition - 1]
						: enabledOptions[enabledOptions.length - 1];
				return focusOption(previousOption);
			}

			if (event.key === 'ArrowDown' && highlightedIndex < 0) {
				const firstGroupSelectAll = getButton(menu.querySelector(GROUP_SELECT_ALL_SELECTOR));
				if (!firstGroupSelectAll) {
					return false;
				}

				return highlightGroupSelectAll(firstGroupSelectAll);
			}

			const highlightedOption = options[highlightedIndex];
			const highlightedGroup = getGroup(highlightedOption);
			if (!highlightedOption || !highlightedGroup) {
				return false;
			}

			const groupEnabledOptions = Array.from(
				highlightedGroup.querySelectorAll<HTMLButtonElement>(ENABLED_OPTION_SELECTOR)
			);
			const shouldFocusCurrentGroupSelectAll =
				event.key === 'ArrowUp' && highlightedOption === groupEnabledOptions[0];
			const groups = Array.from(menu.querySelectorAll<HTMLElement>(GROUP_SELECTOR));
			const currentGroupIndex = groups.indexOf(highlightedGroup);
			const nextGroup = groups
				.slice(currentGroupIndex + 1)
				.concat(groups.slice(0, currentGroupIndex + 1))
				.find(group => group.querySelector(GROUP_SELECT_ALL_SELECTOR));
			const shouldFocusNextGroupSelectAll =
				event.key === 'ArrowDown' &&
				highlightedOption === groupEnabledOptions[groupEnabledOptions.length - 1];
			const groupSelectAll = getButton(
				(shouldFocusCurrentGroupSelectAll ? highlightedGroup : nextGroup)?.querySelector(
					GROUP_SELECT_ALL_SELECTOR
				)
			);

			if (!groupSelectAll || (!shouldFocusCurrentGroupSelectAll && !shouldFocusNextGroupSelectAll)) {
				return false;
			}

			return highlightGroupSelectAll(groupSelectAll);
		},
		[
			clearHighlightedOption,
			focusTargetRef,
			hideHighlight,
			highlightedGroupSelectAllId,
			highlightedIndex,
			menuRef,
			preserveFocus,
			setHighlightedGroupSelectAllId,
			setHighlightedIndex,
			showHighlight,
		]
	);
